import { useNetworkStatus } from '@airship-platform/airship-patterns';
import { PropsWithChildren, useCallback, useRef } from 'react';
import {
  AppointmentProposalOption,
  AppointmentProposalOptionCreate,
  AppointmentProposalOptionsContext,
  AppointmentProposalOptionsProvider,
  useAppointmentProposalOptions,
} from '../../../data-access/appointmentProposalOptions';
import { useOfflineDbData } from '../../rxdb/utils/OfflineDbProvider';
import { createOfflineUUID } from '../../rxdb/utils/rxDbOperations';
import RxDBSetupNotificationAlert from '../../rxdb/utils/RxDBSetupNotificationAlert';
import { useEntityReplication } from '../../rxdb/utils/useEntityReplication';
import { useAppointmentProposalSync } from '../appointmentProposalSync';

const buildOfflineOption = (
  appointmentId: string,
  appointmentProposalId: string,
  create: AppointmentProposalOptionCreate
): AppointmentProposalOption => ({
  appointmentProposalOptionId: createOfflineUUID(),
  jobId: create.jobId,
  appointmentId,
  appointmentProposalId,
  financingPlanId: create.financingPlanId,
  name: create.name,
  optionType: create.optionType,
  entityType: 'APPOINTMENT_PROPOSAL_OPTION',
  status: 'ACTIVE',
  sortOrder: create.sortOrder ?? 0,
  optionFinancingDisabled: create.optionFinancingDisabled ?? false,
  summary: create.summary,
  summaryTextPlain: create.summaryTextPlain,
  leasingPlan: create.leasingPlan,
  availableFinancingPlanIds: create.availableFinancingPlanIds,
  certifications: [],
  externalIds: [],
});

const OfflineAppointmentProposalOptionsOverride: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const context = useAppointmentProposalOptions();
  const data = useOfflineDbData();
  const {
    currentOption,
    upsertOptions,
    settleOptions,
    selectProposalOptions,
    selectOptionId,
    setOptionsMutating,
    updateAppointmentProposalOptionEnrichment:
      updateAppointmentProposalOptionEnrichmentContext,
    buildAppointmentProposalOptionDocumentSignature:
      buildAppointmentProposalOptionDocumentSignatureContext,
  } = context;
  const { syncAppointment } = useAppointmentProposalSync();
  const isOnline = useNetworkStatus();

  const getOptionDocs = useCallback(
    (appointmentProposalId: string) =>
      data.find('appointment_proposal_options', {
        selector: { appointmentProposalId },
      }),
    [data]
  );

  // Ref keeps onSync's identity stable across currentOption changes.
  const currentProposalIdRef = useRef(currentOption?.appointmentProposalId);
  currentProposalIdRef.current = currentOption?.appointmentProposalId;

  const onSync = useCallback(async () => {
    if (currentProposalIdRef.current) {
      const docs = await getOptionDocs(currentProposalIdRef.current);
      upsertOptions(docs);
    }
  }, [getOptionDocs, upsertOptions]);

  const { initError } = useEntityReplication(
    'appointment_proposal_options',
    'airship-appointment-proposal-options',
    { onSync, upsert: upsertOptions }
  );

  const fetchAppointmentProposalOptions: typeof context.fetchAppointmentProposalOptions =
    useCallback(
      async (appointmentId, appointmentProposalId, options) => {
        selectProposalOptions(appointmentProposalId);

        const docs = await getOptionDocs(appointmentProposalId);
        if (docs.length) {
          settleOptions(docs);
        }

        await syncAppointment(appointmentId);
        if (options?.signal?.aborted) {
          return docs;
        }

        const synced = await getOptionDocs(appointmentProposalId);
        settleOptions(synced);

        return synced;
      },
      [settleOptions, selectProposalOptions, syncAppointment, getOptionDocs]
    );

  const fetchAppointmentProposalOption: typeof context.fetchAppointmentProposalOption =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        options
      ) => {
        selectOptionId(appointmentProposalId, appointmentProposalOptionId);

        const getDoc = () =>
          data
            .findOne(
              'appointment_proposal_options',
              appointmentProposalOptionId
            )
            .then((doc) => (doc && doc.status !== 'DELETED' ? doc : undefined));

        const item = await getDoc();
        if (!item && !isOnline()) {
          settleOptions([], { error: 'Option not found offline' });
          return undefined;
        }

        if (item) {
          settleOptions([item]);
        }

        await syncAppointment(appointmentId);

        // Superseded mid-sync (can't cancel the RxDB round trip itself) —
        // see .claude/proposal-option-selection-staleness.md.
        if (options?.signal?.aborted) {
          return item;
        }

        const synced = await getDoc();
        settleOptions(
          synced ? [synced] : [],
          synced ? {} : { error: 'Option not found' }
        );

        return synced;
      },
      [data, settleOptions, selectOptionId, syncAppointment, isOnline]
    );

  const createAppointmentProposalOption: typeof context.createAppointmentProposalOption =
    useCallback(
      (appointmentId, appointmentProposalId, create) => {
        setOptionsMutating();
        return data
          .insertSynced(
            'appointment_proposal_options',
            buildOfflineOption(appointmentId, appointmentProposalId, create),
            0
          )
          .then((res) => {
            settleOptions([res]);
            return res;
          })
          .catch((error) => {
            settleOptions([], {
              error: 'We were unable to add the option. Please try again.',
            });
            throw error;
          });
      },
      [data, setOptionsMutating, settleOptions]
    );

  const createAppointmentProposalOptions: typeof context.createAppointmentProposalOptions =
    useCallback(
      (appointmentId, appointmentProposalId, creates) => {
        setOptionsMutating();
        return data
          .bulkInsertSynced(
            'appointment_proposal_options',
            creates.map((create) =>
              buildOfflineOption(appointmentId, appointmentProposalId, create)
            ),
            0
          )
          .then((res) => {
            settleOptions(res);
            return res;
          })
          .catch((error) => {
            settleOptions([], {
              error: 'We were unable to add the options. Please try again.',
            });
            throw error;
          });
      },
      [data, setOptionsMutating, settleOptions]
    );

  const updateAppointmentProposalOption: typeof context.updateAppointmentProposalOption =
    useCallback(
      (
        _appointmentId,
        _appointmentProposalId,
        appointmentProposalOptionId,
        update
      ) => {
        setOptionsMutating();
        return data
          .patchSynced(
            'appointment_proposal_options',
            appointmentProposalOptionId,
            { ...update, offlineSyncErrors: null },
            0
          )
          .then((res) => {
            settleOptions([res]);
            return res;
          })
          .catch((error) => {
            settleOptions([], {
              error:
                'We were unable to save your option changes. Please try again.',
            });
            throw error;
          });
      },
      [data, setOptionsMutating, settleOptions]
    );

  const updateAppointmentProposalOptionEnrichment: typeof updateAppointmentProposalOptionEnrichmentContext =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId
      ) => {
        if (isOnline()) {
          return updateAppointmentProposalOptionEnrichmentContext(
            appointmentId,
            appointmentProposalId,
            appointmentProposalOptionId
          );
        }
      },
      [isOnline, updateAppointmentProposalOptionEnrichmentContext]
    );

  const buildAppointmentProposalOptionDocumentSignature: typeof buildAppointmentProposalOptionDocumentSignatureContext =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        payload
      ) => {
        if (isOnline()) {
          const option =
            await buildAppointmentProposalOptionDocumentSignatureContext(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              payload
            );

          if (option) {
            await data.upsertSynced('appointment_proposal_options', option);
          }

          return option;
        }
      },
      [data, isOnline, buildAppointmentProposalOptionDocumentSignatureContext]
    );

  if (initError) {
    return (
      <RxDBSetupNotificationAlert
        error={initError}
        entityName="appointment proposal options"
      />
    );
  }

  return (
    <AppointmentProposalOptionsContext.Provider
      value={{
        ...context,
        fetchAppointmentProposalOption,
        fetchAppointmentProposalOptions,
        createAppointmentProposalOption,
        createAppointmentProposalOptions,
        updateAppointmentProposalOption,
        updateAppointmentProposalOptionEnrichment,
        buildAppointmentProposalOptionDocumentSignature,
      }}
    >
      {children}
    </AppointmentProposalOptionsContext.Provider>
  );
};

export const OfflineAppointmentProposalOptionsProvider: React.FC<
  PropsWithChildren
> = ({ children }) => (
  <AppointmentProposalOptionsProvider>
    <OfflineAppointmentProposalOptionsOverride>
      {children}
    </OfflineAppointmentProposalOptionsOverride>
  </AppointmentProposalOptionsProvider>
);
